import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { track } from "../lib/analytics";

export default function Portfolio() {
  usePageMeta({
    title: "Interior Fit-Out Projects Dubai & Sharjah | Time Furniture Portfolio",
    description:
      "Browse completed interior fit-out, renovation and joinery projects across the UAE — villas, apartments, offices and holiday homes by Time Furniture. Since 1993.",
    canonical: "https://timefurniture.ae/portfolio",
    ogImage: "/projects/palm-tower-1.webp",
  });
  useEffect(() => { track.portfolioView({ source: "portfolio_page" }); }, []);
  const [filter, setFilter] = useState("All");
  const items = useMemo(
    () =>
      filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter),
    [filter]
  );

  return (
    <div data-testid="portfolio-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        testid="portfolio-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Portfolio" },
        ]}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-10">
        <p className="eyebrow mb-6">Portfolio · Projects Delivered End-to-End</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl text-[#1C110C]">
          Spaces we've <em className="text-[#6E1F2C] not-italic">shaped</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-[#3E2A22] leading-relaxed text-lg">
          Turnkey fit-outs, renovations, MEP and bespoke joinery — delivered as complete interior solutions across the UAE.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8">
        <div className="flex flex-wrap gap-2 md:gap-3 border-t border-b border-[#6E1F2C]/15 py-6">
          {PORTFOLIO_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-[11px] tracking-[0.28em] uppercase border transition-all ${
                filter === c
                  ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                  : "border-[#6E1F2C]/25 text-[#7A6B5F] hover:text-[#1C110C] hover:border-[#6E1F2C]/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <Link
              key={p.id}
              to={`/portfolio/${p.id}`}
              data-testid={`portfolio-item-${p.id}`}
              className={`group overflow-hidden animate-fade-up block`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="overflow-hidden h-80 md:h-96 relative">
                <img
                  src={p.image}
                  alt={`${p.title} — interior fit-out project in ${p.location || "UAE"} by Time Furniture`}
                  className="w-full h-full object-cover img-zoom"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/35 transition-colors" />
                {p.pdf && (
                  <span
                    data-testid={`portfolio-pdf-badge-${p.id}`}
                    className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#1C110C]/85 text-[#D4A963] backdrop-blur-sm text-[10px] tracking-[0.28em] uppercase px-3 py-1.5"
                  >
                    PDF · Case Study
                  </span>
                )}
                <div className="absolute top-4 right-4 w-10 h-10 bg-[#FAF5EC] text-[#6E1F2C] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight size={16} />
                </div>
              </div>
              <div className="pt-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
                  {p.category} · {p.year}
                </p>
                <p className="font-serif-display text-2xl mt-2 text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">{p.title}</p>
              </div>
            </Link>
          ))}
        </div>
        {items.length === 0 && (
          <p className="text-center text-[#7A6B5F] py-20">No projects in this category yet.</p>
        )}
      </section>

      {/* Closing CTA → Contact */}
      <section
        data-testid="portfolio-closing-cta"
        className="bg-[#F2E9D8] border-t border-[#6E1F2C]/15"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <p className="eyebrow mb-5">Next Step</p>
          <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl mx-auto">
            See a project that resonates?
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-[#3E2A22] leading-relaxed text-base md:text-lg">
            Tell us about your space — we&rsquo;ll reply within 24 hours with an
            indicative scope and timeline.
          </p>
          <Link
            to="/contact"
            data-testid="portfolio-link-contact"
            className="mt-12 inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
          >
            Contact our fit-out team
          </Link>
        </div>
      </section>
    </div>
  );
}
